/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
  useResizeEffect,
} from '@web-stories-wp/react';
import { useFeature } from 'flagged';
import { __ } from '@web-stories-wp/i18n';
import { trackEvent } from '@web-stories-wp/tracking';

/**
 * Internal dependencies
 */
import { useAPI } from '../../app/api';
import { useStory } from '../../app/story';
import { useHighlights } from '../../app/highlights';
import { useUser } from '@hooks';
import { AUTOMATE, DOCUMENT, DESIGN, REFINE } from './constants';
import Context from './context';
import DesignInspector from './design';
import DocumentInspector from './document';
import RefineInspector from './refine';
import AutomateInspector from './automate';

const INSPECTOR_TAB_IDS = new Set([DOCUMENT, DESIGN, REFINE, AUTOMATE]);
function InspectorProvider({ children }) {
  const {
    actions: { getAuthors },
  } = useAPI();
  const { selectedElementIds, currentPage } = useStory(({ state }) => ({
    selectedElementIds: state.selectedElementIds,
    currentPage: state.currentPage,
  }));
  const useStudioAIAssistant = useFeature('useStudioAIAssistant');

  const { tab: highlightedTab } = useHighlights(({ tab }) => ({ tab }));

  useEffect(() => {
    if (INSPECTOR_TAB_IDS.has(highlightedTab)) {
      setTab(highlightedTab);
      trackEvent('quick_action_tab_change', {
        name: highlightedTab,
      });
    }
  }, [highlightedTab]);

  const inspectorRef = useRef(null);

  const [tab, setTab] = useState(DESIGN);
  const [users, setUsers] = useState([]);
  const user = useUser();
  const [inspectorContentHeight, setInspectorContentHeight] = useState(null);
  const inspectorContentRef = useRef();
  const tabRef = useRef(tab);

  const designPaneRef = useRef(null);
  const documentPaneRef = useRef(null);
  const refinePanelRef = useRef(null);
  const automatePanelRef = useRef(null);

  const tabRefs = useMemo(
    () => ({
      [DESIGN]: designPaneRef,
      [DOCUMENT]: documentPaneRef,
      [REFINE]: refinePanelRef,
      [AUTOMATE]: automatePanelRef,
    }),
    []
  );

  const [isUsersLoading, setIsUsersLoading] = useState(false);

  const setInspectorContentNode = useCallback((node) => {
    inspectorContentRef.current = node;
  }, []);

  useResizeEffect(
    inspectorContentRef,
    ({ height }) => setInspectorContentHeight(height),
    []
  );

  useEffect(() => {
    if (selectedElementIds.length > 0 && tabRef.current === DOCUMENT) {
      setTab(DESIGN);
    }
  }, [selectedElementIds]);

  useEffect(() => {
    if (tabRef.current === DOCUMENT) {
      setTab(DESIGN);
    }
  }, [currentPage]);

  useEffect(() => {
    tabRef.current = tab;
  }, [tab]);

  const loadUsers = useCallback(() => {
    if (!isUsersLoading && users.length === 0) {
      setIsUsersLoading(true);
      getAuthors()
        .then((data) => {
          const saveData = data.map(({ id, name }) => ({
            id,
            name,
          }));
          setUsers(saveData);
        })
        .finally(() => {
          setIsUsersLoading(false);
        });
    }
  }, [isUsersLoading, users.length, getAuthors]);

  const tabs = useMemo(() => {
    const tabsForUser = [
      {
        id: DESIGN,
        title: __('Design', 'web-stories'),
        Pane: DesignInspector,
      },
      {
        id: DOCUMENT,
        title: __('Document', 'web-stories'),
        Pane: DocumentInspector,
      },
    ];

    if (useStudioAIAssistant) {
      tabsForUser.push({
        id: REFINE,
        title: __('Refine', 'web-stories'),
        Pane: RefineInspector,
      });
    }

    if (user.isSystemAdmin) {
      tabsForUser.push({
        id: AUTOMATE,
        title: __('Automate', 'web-stories'),
        Pane: AutomateInspector,
      });
    }

    return tabsForUser;
  }, [useStudioAIAssistant, user.isSystemAdmin]);

  const state = {
    state: {
      tab,
      tabRefs,
      users,
      inspectorContentHeight,
      isUsersLoading,
    },
    refs: {
      inspector: inspectorRef,
    },
    actions: {
      setTab,
      loadUsers,
      setInspectorContentNode,
    },
    data: {
      tabs,
    },
  };

  return <Context.Provider value={state}>{children}</Context.Provider>;
}

InspectorProvider.propTypes = {
  children: PropTypes.node,
};

export default InspectorProvider;
